import cx from "clsx"
import { Card, ComponentState, Flex, Skeleton, Spinner, Text, Tooltip } from "components/common"
import { IconText } from "components/custom"
import { InterestedStarIcon } from "components/svg"
import { useCallback, useEffect, useMemo } from "react"
import { joinBytItems, localisedValue } from "utils"
import theme from "utils/themeVars"
import styles from "../index.module.less"
import { useTranslation } from "react-i18next"
import { truncateText } from "utils/reports"

const ProjectCardItem = ({ project, isSelected, onSelect }) => {
  const { t } = useTranslation()
  const projectName = useMemo(() => localisedValue({ object: project, key: "name" }), [project])
  const locationDisplay = useMemo(() => {
    const location = localisedValue({ object: project?.Location, key: "name" })
    const city = localisedValue({ object: project?.Address?.CategoryCity, key: "name" })
    return joinBytItems([location, city], ", ")
  }, [project])

  return (
    <Card
      bodyStyle={{ padding: "12px 16px" }}
      padding="12px"
      onClick={onSelect}
      className={cx(styles.projectCard, "pointer", {
        [styles.projectCardSelected]: isSelected,
      })}
      style={{ "--ant-border-radius-lg": "8px", boxShadow: "none" }}
    >
      <Flex vertical gap={4} align="start">
        {project.interestedProject && (
          <Tooltip title={t("Client's Interested Project")}>
            <div className={cx(styles.interestStar, "flipX")}>
              <InterestedStarIcon />
            </div>
          </Tooltip>

        )}
        <Text type={isSelected && "primary"} strong className={styles.projectName}>
          <Tooltip title={projectName?.length > 30 && projectName}>{truncateText(projectName, 30)}</Tooltip>
        </Text>
        <IconText
          gap={4}
          textType="secondaryLight"
          icon="MdLocationPin"
          title={locationDisplay}
          iconProps={{
            size: "12px",
            color: theme["gray800"],
          }}
          titleProps={{ style: { fontSize: "12px" } }}
        />
      </Flex>
    </Card>
  )
}

const ProjectCard = ({
  projects,
  selectedProjectId,
  onSelectProject,
  isFiltersReset,
  systemProjects,
  isLoading,
  error,
  onRetry = () => {},
  filterProjectId,
}) => {
  const handleSelect = useCallback(project => () => onSelectProject(project), [onSelectProject])

  // Compute final projects array based on business logic
  const finalProjects = useMemo(() => {
    // Case 1: If isFiltersReset is true, show all projects regardless of systemProjects
    if (isFiltersReset) {
      return projects || []
    }

    // Case 2: If systemProjects is undefined (API not called yet), show all projects
    if (systemProjects === undefined) {
      return projects || []
    }

    // Case 3: systemProjects exists (empty array or with data)
    // Build map of interested project IDs while filtering
    const interestedProjectIdsMap = {}
    const interestedProjects = (projects || []).filter(project => {
      if (project?.interestedProject) {
        interestedProjectIdsMap[project.projectId] = true
        return true
      }
      return false
    })

    // Extract systemProjects array from systemProjects.projects
    const systemProjectsArray = Array.isArray(systemProjects?.projects) ? systemProjects.projects : []

    // Filter system projects to exclude ones already in interested projects map
    const filteredSystemProjects = systemProjectsArray.filter(p => !interestedProjectIdsMap[p?.projectId])

    return [...interestedProjects, ...filteredSystemProjects]
  }, [projects, systemProjects, isFiltersReset])

  // Auto-select first project when finalProjects changes
  useEffect(() => {
    if (finalProjects?.length > 0) {
      const firstProject = finalProjects[0]

      // If filterProjectId is set, check if it exists in finalProjects
      if (filterProjectId) {
        // Wait until systemProjects has loaded and not currently loading
        // isLoading means a new API call is in progress (could have stale data)
        if (!systemProjects?.projects || isLoading) {
          return // Wait for systemProjects to load
        }

        const isFilterProjectInArray = finalProjects.some(p => p.projectId === +filterProjectId)
        // If filter project is not in the array after systemProjects loaded, select the first one
        if (!isFilterProjectInArray) {
          onSelectProject(firstProject)
        }
        return
      }

      // Select first project if:
      // 1. Nothing is currently selected, OR
      // 2. Current selected project is not in the new array
      const isCurrentSelectedInArray = selectedProjectId && finalProjects.some(p => p.projectId === selectedProjectId)
      if (!selectedProjectId || !isCurrentSelectedInArray) {
        onSelectProject(firstProject)
      }
    }
  }, [finalProjects, selectedProjectId, onSelectProject, filterProjectId, systemProjects, isLoading])

  if (isLoading) {
    return (
      <Flex gap={"16px"} className="w-100">
        {[1, 2, 3, 4].map(num => (
          <Skeleton key={num} height={60} width={220} />
        ))}
      </Flex>
    )
  }

  if (error) {
    return (
      <ComponentState
        description={error?.message}
        status={error?.status}
        btnLoading={isLoading}
        onClick={onRetry}
        className="w-100"
      />
    )
  }

  if (!finalProjects?.length) return null

  return (
    <Flex className={`${styles.projectScrollContainer} hide-scrollbar`}>
      {finalProjects.map(project => (
        <ProjectCardItem
          key={project.projectId}
          project={project}
          isSelected={selectedProjectId === project.projectId}
          onSelect={handleSelect(project)}
        />
      ))}
    </Flex>
  )
}

export default ProjectCard
